import { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useGetParcelAccounts } from "api/queries/user/useGetParcelAccounts";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import Button from "components/v2/Button/Button";
import DataTable from "components/v2/Table/DataTable";
import Tablefooter from "components/v2/Tablefooter/Tablefooter";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import EmptyParcelAccountState from "./EmptyParcelAccountState";
import getParcelAccountColumns from "./ParcelAccountColumns";
import "./ParcelAccounts.scss";

export default function ParcelAccounts() {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const { admin_settings } = JSON.parse(permissions || "{}")?.permissions || {};

  useEffect(() => {
    if (!admin_settings?.create_edit_parcel_account) {
      navigate("/settings/your-details");
    }
  }, [admin_settings]);

  const columns = useMemo(() => getParcelAccountColumns(intl), [intl]);
  const {
    loading: loadingParcelAccounts,
    instance,
    totalPages,
    currentPage,
    totalData,
    itemsPerPage,
    setItemsPerPage,
    setCurrentPage,
  } = useTableQueryRequest(useGetParcelAccounts, columns, {
    params: () => ({
      sort_by: null,
      page: searchParams.get("page") || "1",
    }),
  });

  const handleClickAddAccount = () => {
    // Logic to handle adding a new parcel account
    navigate("/settings/parcel-accounts/add");
  };

  return (
    <>
      <h3 className="mt-3xl">
        <FormattedMessage id="settings.parcelAccounts" defaultMessage="Parcel accounts" />
      </h3>
      <div className="mt-xs text-tertiary text-md font-regular mb-4xl">
        <FormattedMessage
          id="settings.parcelAccounts.manageYourParcelAccountsHere"
          defaultMessage="Manage your parcel accounts here"
        />
      </div>

      {loadingParcelAccounts ? (
        <DataTable tableInstance={instance} isLoading />
      ) : totalData > 0 ? (
        <div className="parcel-accounts-table-container">
          <div className="d-flex justify-content-between gap-lg align-items-center py-xl px-3xl bg-primary">
            <div className="text-md font-medium text-primary">
              <FormattedMessage id="settings.parcelAccounts.yourAccounts" defaultMessage="Your accounts" />
            </div>
            <Button
              label={intl.formatMessage({
                id: "settings.parcelAccounts.addAccount",
                defaultMessage: "Add account",
              })}
              iconLeft={<PlusIcon />}
              size="sm"
              onClick={handleClickAddAccount}
            />
          </div>

          <DataTable
            tableInstance={instance}
            tableLayout="fixed"
            onRowClick={(row) => navigate(`/settings/parcel-accounts/${row.original.id}`)}
          />
          <Tablefooter
            totalPages={totalPages}
            totalData={totalData}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            pageClicked={(page) => setCurrentPage(page)}
          />
        </div>
      ) : (
        <EmptyParcelAccountState onClickAddAccount={handleClickAddAccount} />
      )}
    </>
  );
}
